/**
 * ProcessStatistics widget — per-status counts of process instances.
 *
 * Migrated to live API in Wave 3.6. Reads GET /v1/processes/instances
 * once and aggregates client-side by status + time window. The backend
 * endpoint doesn't yet support \`?since=\` or \`?time_range=\` filters, so
 * the Week/Month/Quarter tabs filter the client-side instance list by
 * each row's created_at.
 *
 * Previous-period comparison is also client-side: for the selected
 * window we compute the same-length window immediately preceding it
 * and compare the count delta to render the up/down trend.
 *
 * Status buckets preserve the mock's six slices, mapped from whatever
 * backend returns:
 *   'active'      → status in ('running', 'pending', 'new', 'in_progress')
 *   'archived'    → status == 'archived'
 *   'exceptions'  → status in ('failed', 'error', 'exception')
 *   'completed'   → status in ('completed', 'approved')
 *   'overdue'     → sla_status == 'breached'
 *   'critical'    → priority == 'high' or 'urgent'
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, CircularProgress, Grid, Paper, Tab, Tabs, Typography,
} from '@mui/material';
import {
  CheckCircle, Error, Pending, TrendingDown, TrendingUp,
} from '@mui/icons-material';
import { ApiError, processesApi } from '../../../../api';
import { WidgetErrorState } from '../../../../components/primitives';
import { queryDashboard } from '../../../../api/dashboardQuery';
import type { ProcessInstance } from '../../../../api/processes';
import { useTranslation } from 'react-i18next';
import { translateApiError } from '../../../../i18n/translateApiError';

type TimeRange = 'week' | 'month' | 'quarter';

interface Bucket {
  label: string;
  value: number;
  previousValue: number;
  color: string;
  icon: React.ReactNode;
}

const ACTIVE = new Set(['running', 'pending', 'new', 'in_progress']);
const COMPLETED = new Set(['completed', 'approved']);
const EXCEPTIONS = new Set(['failed', 'error', 'exception']);

function windowDays(r: TimeRange): number {
  if (r === 'week') return 7;
  if (r === 'month') return 30;
  return 90;
}

function inWindow(row: ProcessInstance, sinceMs: number, untilMs: number): boolean {
  const created = Date.parse(row.created_at ?? '') || 0;
  return created >= sinceMs && created < untilMs;
}

function bucketize(rows: ProcessInstance[]): {
  active: number;
  archived: number;
  exceptions: number;
  completed: number;
  overdue: number;
  critical: number;
} {
  let active = 0;
  let archived = 0;
  let exceptions = 0;
  let completed = 0;
  let overdue = 0;
  let critical = 0;
  for (const r of rows) {
    const s = r.status ?? '';
    if (ACTIVE.has(s)) active += 1;
    else if (s === 'archived') archived += 1;
    else if (EXCEPTIONS.has(s)) exceptions += 1;
    else if (COMPLETED.has(s)) completed += 1;
    if (r.sla_status === 'breached') overdue += 1;
    if (r.priority === 'high' || r.priority === 'urgent') critical += 1;
  }
  return { active, archived, exceptions, completed, overdue, critical };
}

const ProcessStatistics: React.FC = () => {
  const { t } = useTranslation();
  const [rows, setRows] = useState<ProcessInstance[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timeRange, setTimeRange] = useState<TimeRange>('week');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await queryDashboard(
          'process-instances',
          () => processesApi.processInstancesApi.list(),
        );
        if (!cancelled) setRows(data);
      } catch (e) {
        if (!cancelled) setError(e instanceof ApiError ? translateApiError(e, t) : t('dashboard:widgets.processStatistics.loadError'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [t]);

  const buckets: Bucket[] = useMemo(() => {
    const days = windowDays(timeRange);
    const now = Date.now();
    const DAY = 86_400_000;
    const currentWindow = rows.filter((r) => inWindow(r, now - days * DAY, now));
    const prevWindow = rows.filter((r) => inWindow(r, now - 2 * days * DAY, now - days * DAY));
    const cur = bucketize(currentWindow);
    const prev = bucketize(prevWindow);
    return [
      { label: t('dashboard:widgets.processStatistics.active'), value: cur.active, previousValue: prev.active, color: '#2196F3', icon: <Pending sx={{ color: '#2196F3' }} /> },
      { label: t('dashboard:widgets.processStatistics.archived'), value: cur.archived, previousValue: prev.archived, color: '#4CAF50', icon: <CheckCircle sx={{ color: '#4CAF50' }} /> },
      { label: t('dashboard:widgets.processStatistics.exceptions'), value: cur.exceptions, previousValue: prev.exceptions, color: '#F44336', icon: <Error sx={{ color: '#F44336' }} /> },
      { label: t('dashboard:widgets.processStatistics.completed'), value: cur.completed, previousValue: prev.completed, color: '#9C27B0', icon: <CheckCircle sx={{ color: '#9C27B0' }} /> },
      { label: t('dashboard:widgets.processStatistics.overdue'), value: cur.overdue, previousValue: prev.overdue, color: '#FF9800', icon: <Error sx={{ color: '#FF9800' }} /> },
      { label: t('dashboard:widgets.processStatistics.critical'), value: cur.critical, previousValue: prev.critical, color: '#795548', icon: <Error sx={{ color: '#795548' }} /> },
    ];
  }, [rows, timeRange, t]);

  const maxValue = Math.max(1, ...buckets.map((b) => b.value));

  if (loading) {
    return (
      <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return <WidgetErrorState error={error} />;
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ mb: 1.5 }}>
        <Tabs value={timeRange} onChange={(_, v) => setTimeRange(v as TimeRange)}
          sx={{ minHeight: 32,
            '& .MuiTab-root': { minHeight: 32, minWidth: 'auto', px: 1.5, py: 0,
              textTransform: 'none', fontSize: '0.8125rem', fontWeight: 600 } }}>
          <Tab label={t('dashboard:widgets.processStatistics.tabWeek')} value="week" />
          <Tab label={t('dashboard:widgets.processStatistics.tabMonth')} value="month" />
          <Tab label={t('dashboard:widgets.processStatistics.tabQuarter')} value="quarter" />
        </Tabs>
      </Box>

      <Grid container spacing={1} sx={{ mb: 1.5 }}>
        {buckets.map((b) => {
          const up = b.value >= b.previousValue;
          const delta = b.previousValue === 0
            ? (b.value === 0 ? 0 : 100)
            : Math.abs(((b.value - b.previousValue) / b.previousValue) * 100);
          return (
            <Grid item xs={4} key={b.label}>
              <Paper elevation={0} sx={{ p: 1, borderRadius: 2, bgcolor: 'background.paper' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ width: 28, height: 28, borderRadius: 1, display: 'flex',
                    alignItems: 'center', justifyContent: 'center', mr: 1 }}>
                    {b.icon}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.primary', display: 'block', lineHeight: 1.2 }}>
                      {b.label}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {/* No `cursor: 'pointer'` and no hover underline: three of
                          these six buckets (archived / overdue / critical) have no
                          page to open — there are only active, completed and failed
                          instance routes — so half the grid would have to lie or
                          land somewhere it does not describe. An affordance is a
                          claim; the number stays a number until the destinations
                          exist. */}
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: b.color }}>
                        {b.value}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center',
                        color: up ? 'success.main' : 'error.main', typography: 'caption' }}>
                        {up ? <TrendingUp sx={{ fontSize: '0.85rem' }} />
                          : <TrendingDown sx={{ fontSize: '0.85rem' }} />}
                        <Typography variant="caption" sx={{ fontSize: '0.7rem', ml: 0.25 }}>
                          {delta.toFixed(0)}%
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          );
        })}
      </Grid>

      <Box sx={{ flex: 1, display: 'flex', alignItems: 'flex-end', gap: 1, minHeight: 0 }}>
        {buckets.map((b) => (
          <Box key={b.label} sx={{ flex: 1, display: 'flex', flexDirection: 'column',
            alignItems: 'center', gap: 0.5, height: '100%', justifyContent: 'flex-end' }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, fontSize: '0.7rem' }}>
              {b.value}
            </Typography>
            <Box sx={{ width: '100%', bgcolor: `${b.color}25`, borderRadius: '3px 3px 0 0',
              position: 'relative', height: `${(b.value / maxValue) * 100}%`, minHeight: 4,
              transition: 'height 0.3s ease', '&:hover': { bgcolor: `${b.color}40` } }}>
              <Box sx={{ position: 'absolute', bottom: 0, left: 0, width: '100%', height: 3,
                bgcolor: b.color, borderRadius: '0 0 0 0' }} />
            </Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 600,
              color: 'text.primary', whiteSpace: 'nowrap', overflow: 'hidden',
              textOverflow: 'ellipsis', maxWidth: '100%' }}>
              {b.label}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default ProcessStatistics;
